import type { AuctionSessionBidLot } from '@carsfinders/contracts';

import { t } from '../../../../lib/i18n';
import { formatChf, lotSiteHref, relayStatusTranslationKey } from '../../../bids/utils';
import { bidLotLeaderLine, bidLotTitle, bidsLabel } from './session-schedule-utils';

/**
 * The lots WITH CLIENT BIDS inside one session (owner decision 2026-10-12):
 * when each closes, how many standing bids it has, who leads and the leading
 * bid's relay status. Rendered under a session in the day panel and the list,
 * with the lot's photo first (owner, 2026-10-13: «додай зображення лоту»). The
 * whole row opens the car on the auction site, in a new tab.
 *
 * Renders nothing for a session without `bidLots` — the field only exists on
 * the `scope=bids` response.
 */
export function SessionBidLots({ lots }: { lots: AuctionSessionBidLot[] | undefined }) {
  if (!lots || lots.length === 0) return null;

  return (
    <ul className="flex flex-col gap-1" data-testid="session-bid-lots">
      {lots.map((lot) => (
        <li key={lot.carId}>
          <a
            href={lotSiteHref({ carId: lot.carId, listingId: lot.listingId })}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="session-bid-lot-link"
            className="grid grid-cols-[60px_44px_1fr_auto] items-center gap-2 rounded-cf-sm text-[12px] leading-[1.4] hover:bg-cf-surface-sunken focus-visible:outline-2 focus-visible:outline-cf-brand"
          >
          {lot.imageUrl ? (
            <img
              src={lot.imageUrl}
              alt=""
              loading="lazy"
              data-testid="session-bid-lot-photo"
              className="h-10 w-[60px] rounded-cf-sm bg-cf-fill object-cover"
            />
          ) : (
            <span aria-hidden className="h-10 w-[60px] rounded-cf-sm bg-cf-fill" />
          )}
          <span className="font-mono text-cf-ink-subtle">{lot.time}</span>
          <span className="min-w-0">
            <span className="block truncate font-semibold text-cf-ink">{bidLotTitle(lot, t)}</span>
            <span className="block truncate text-cf-ink-subtle">
              {`${bidsLabel(lot.bidCount, t)} · ${bidLotLeaderLine(lot, t)} · ${formatChf(lot.leader.amount)}`}
            </span>
          </span>
          <span className="rounded-cf-xs border border-cf-line-soft px-1.5 py-0.5 text-[11px] whitespace-nowrap text-cf-ink-muted">
            {t(relayStatusTranslationKey(lot.leader.relayStatus))}
          </span>
          </a>
        </li>
      ))}
    </ul>
  );
}
